import { createSelector } from '@reduxjs/toolkit'
import { has } from 'lodash'
import Sort from 'beam/dist/utils/sort'
import chroma from 'chroma-js'
import { EVAL_STATE } from './evaluationForm.constants'
import { Eval, EvalComment, EvaluationData } from './evaluationForm.api'

const activeFormsSelector = (state: $TSFIXME) => state.activeForms.data
export const templateSelector = (state: EvaluationData) => state.template.data
const valuesSelector = (state: EvaluationData) => state.values.data
const commentsSelector = (state: EvaluationData) => state.comments.data

export const sanitizeValues = createSelector(templateSelector, valuesSelector, (template, values): Eval | null | undefined =>
  // @ts-expect-error TODO: fix error
  values === null && Boolean(template)
    ? {
      id: null,
      state: { stateId: EVAL_STATE.UNSCORED },
      // @ts-expect-error TODO: handle null template
      evalForm: { evalFormId: template.id },
      // @ts-expect-error TODO: handle null template
      sections: template.sections.map(section => ({
        id: section.id,
        questions: section.questions.map(question => ({ id: question.id, selectedOption: null })),
      })),
    }
    : values,
)

export const getChartData = createSelector(templateSelector, sanitizeValues, (template, values) => {
  // @ts-expect-error TODO: handle nullish values and template
  if (template !== null && values.state.stateId !== EVAL_STATE.UNSCORED && template.id === values.evalForm.evalFormId && values.totalScore !== void 0) {
    return {
      // @ts-expect-error TODO: handle nullish values and template
      additiveScore: values.additiveScore,
      // @ts-expect-error TODO: handle nullish values
      totalScore: values.totalScore,
      // @ts-expect-error TODO: handle undefined template
      sections: template.sections.map(section => {
        const colors = chroma
          .scale([section.startColor, section.endColor])
          .mode('lch')
          .colors(section.questions.length)

        return {
          id: section.id,
          name: section.name,
          // @ts-expect-error TODO: handle nullish values
          additiveScore: values.sections.find(({ id }) => id === section.id).additiveScore,
          // @ts-expect-error TODO: handle nullish values
          totalScore: values.sections.find(({ id }) => id === section.id).totalScore,
          questions: section.questions.map((question, index) => {
            return {
              id: question.id,
              text: question.text,
              // @ts-expect-error TODO: handle nullish values
              additiveScore: values.sections.find(({ id }) => id === section.id).questions.find(({ id }) => id === question.id).additiveScore,
              // @ts-expect-error TODO: handle nullish values
              maxAdditiveScore: values.sections.find(({ id }) => id === section.id).questions.find(({ id }) => id === question.id).maxAdditiveScore,
              background: colors[index],
              // @ts-expect-error TODO: handle undefined color[index]
              color: chroma.contrast('white', colors[index]) < 4.5 ? '#000' : '#fff',
              // @ts-expect-error TODO: handle undefined color[index]
              stroke: chroma.contrast(colors[index], '#fff') < 4.5 ? 'rgba(0,0,0,0.38)' : 'transparent',
            }
          }),
        }
      }),
    }
  }
  return null
})

export const getDefaultAnswers = createSelector(templateSelector, template =>
  template === null
    ? null
    // @ts-expect-error TODO: handle undefined template
    : template.sections.reduce<Record<number, number>>((acc, { questions }) => {
      questions.forEach(({ options, id }) => {
        // @ts-expect-error TODO: handle undefined .find()
        acc[id] = options.find(answer => answer.isDefault).id
      })
      return acc
    }, {}),
)

export const hasValidAnswers = createSelector(valuesSelector, values => {
  if (values !== null) {
    // @ts-expect-error TODO: handle undefined values
    return !values.sections.some(({ questions }) =>
      questions.some(
        ({ selectedOption }) => selectedOption === null || typeof selectedOption === 'undefined',
      ),
    )
  }
  return false
})

const getActiveFormsForDropdown = createSelector(activeFormsSelector, activeForms =>
  // @ts-expect-error TODO: fix implicit any form
  activeForms.map(form => ({ id: form.id, name: form.name })),
)

export const getAlphabetizedActiveForms = createSelector(getActiveFormsForDropdown, activeForms =>
  activeForms.sort(Sort.sortFieldBy('name', Sort.sortByString)),
)

export const isEvaluationLoaded = createSelector(
  (state: EvaluationData) => state.template,
  (state: EvaluationData) => state.values,
  (state: EvaluationData) => state.comments,
  (template, values, comments) => {
    return (!template.isFetching || Boolean(template.data)) &&
    (!values.isFetching || Boolean(values.data)) &&
    (!comments.isFetching || Boolean(comments.data))
  },
)

const formCommentsSelector = createSelector(commentsSelector, data =>
  // @ts-expect-error TODO: handle nullish data
  data.length === 0 ? [] : data.filter(comment => !has(comment, 'sectionFK')).sort((a, b) => a.created - b.created),
)

const sectionCommentsSelector = createSelector(commentsSelector, data =>
  // @ts-expect-error TODO: handle nullish data
  data.length === 0
    ? []
    // @ts-expect-error TODO: handle nullish data
    : data
      .filter(comment => has(comment, 'sectionFK') && !has(comment, 'questionFK'))
      .sort((a, b) => a.created - b.created),
)
const questionCommentsSelector = createSelector(commentsSelector, data =>
  // @ts-expect-error TODO: handle nullish data
  data.length === 0
    ? []
    // @ts-expect-error TODO: handle nullish data
    : data
      .filter(comment => has(comment, 'sectionFK') && has(comment, 'questionFK'))
      .sort((a, b) => a.created - b.created),
)

const indexSectionComments = createSelector(sectionCommentsSelector, comments =>
  comments.length === 0
    ? []
    : comments.reduce<Record<number, EvalComment[]>>((acc, comment) => {
      // @ts-expect-error TODO: handle undefined sectionFK
      if (!has(acc, comment.sectionFK)) {
        // @ts-expect-error TODO: handle undefined sectionFK
        acc[comment.sectionFK] = []
      }
      // @ts-expect-error TODO: handle undefined sectionFK
      acc[comment.sectionFK].push(comment)
      return acc
    }, {}),
)

const indexQuestionComments = createSelector(questionCommentsSelector, comments =>
  comments.length === 0
    ? []
    : comments.reduce<Record<number, EvalComment[]>>((acc, comment) => {
      // @ts-expect-error TODO: handle undefined questionFK
      if (!has(acc, comment.questionFK)) {
        // @ts-expect-error TODO: handle undefined questionFK
        acc[comment.questionFK] = []
      }
      // @ts-expect-error TODO: handle undefined questionFK
      acc[comment.questionFK].push(comment)
      return acc
    }, {}),
)

export const indexComments = createSelector(
  formCommentsSelector,
  indexSectionComments,
  indexQuestionComments,
  (formComments, sectionComments, questionComments) => ({
    formComments,
    sectionComments,
    questionComments,
  }),
)
